import { useState } from "react";
import { useSession } from "../lib/session.tsx";

/**
 * First-run setup tour. Shows once (localStorage flag), relaunchable from
 * Settings via a window "open-wizard" event. Content is inline-bilingual to
 * avoid hundreds of one-off i18n keys for prose that only appears here.
 */

const DONE_KEY = "arturo-wizard-done";

function doneKey(uid?: string): string {
  return uid ? `${DONE_KEY}:${uid}` : DONE_KEY;
}

export function wizardDone(uid?: string): boolean {
  return localStorage.getItem(doneKey(uid)) === "true";
}

interface Step {
  icon: string;
  title: { en: string; es: string };
  body: { en: string; es: string };
}

const STEPS: Step[] = [
  {
    icon: "🧭",
    title: { en: "Welcome to CampaignOS", es: "Bienvenido a CampaignOS" },
    body: {
      en: "Your campaign's single system of record — people, work, evidence, field ops, finance, and an AI strategy advisor, all in one place.",
      es: "El sistema único de tu campaña — personas, trabajo, evidencia, territorio, finanzas y un asesor de estrategia con IA, todo en un lugar.",
    },
  },
  {
    icon: "✅",
    title: { en: "The advisor is grounded", es: "El asesor está fundamentado" },
    body: {
      en: "It answers only from evidence you've approved, cites every fact, and refuses rather than making things up. An honest 'I can't answer that yet' is the correct output.",
      es: "Responde solo con evidencia que aprobaste, cita cada hecho y se niega en vez de inventar. Un honesto 'aún no puedo responder eso' es la salida correcta.",
    },
  },
  {
    icon: "📄",
    title: { en: "Feed it evidence", es: "Aliméntalo con evidencia" },
    body: {
      en: "Upload documents, audio, or video under Evidence. After extraction, approve an excerpt and set a reliability tier — only then can the advisor cite it.",
      es: "Sube documentos, audio o video en Evidencia. Tras la extracción, aprueba un extracto y define su nivel de confiabilidad — solo entonces el asesor puede citarlo.",
    },
  },
  {
    icon: "🧠",
    title: { en: "Four kinds of memory", es: "Cuatro tipos de memoria" },
    body: {
      en: "Memory organizes what you know: facts (cited), timeline events, strategy playbooks, and this session's focus. Facts stay grounded; strategy stays clearly labeled as recommendation.",
      es: "La memoria organiza lo que sabes: hechos (citados), cronología, manuales de estrategia y el enfoque de esta sesión. Los hechos quedan fundamentados; la estrategia queda marcada como recomendación.",
    },
  },
  {
    icon: "📡",
    title: { en: "Watch the narrative", es: "Vigila la narrativa" },
    body: {
      en: "The Watchdog sweeps the public web three times a day and the Anecdotal Meter tracks each topic climbing from blog chatter toward news — so you see a story breaking before it breaks.",
      es: "El Vigilante rastrea la web pública tres veces al día y el Medidor Anecdótico rastrea cada tema subiendo de rumor a noticia — para que veas una historia estallar antes de que estalle.",
    },
  },
  {
    icon: "✦",
    title: { en: "Your assistant is always on", es: "Tu asistente siempre activo" },
    body: {
      en: "The ✦ button in the corner reads your live data and suggests the highest-leverage next move. Settings holds the theme, language, and the full user guide.",
      es: "El botón ✦ en la esquina lee tus datos en vivo y sugiere el siguiente paso de mayor impacto. En Ajustes están el tema, el idioma y la guía completa.",
    },
  },
];

export function Wizard({ onClose }: { onClose: () => void }) {
  const { locale, t, firebaseUser } = useSession();
  const [i, setI] = useState(0);
  const step = STEPS[i]!;

  function finish() {
    localStorage.setItem(doneKey(firebaseUser?.uid), "true");
    onClose();
  }

  return (
    <div className="wiz-overlay" role="dialog" aria-modal="true">
      <div className="wiz-modal">
        <div style={{ fontSize: "2.4rem" }}>{step.icon}</div>
        <h2 style={{ margin: "8px 0" }}>{step.title[locale]}</h2>
        <p className="muted">{step.body[locale]}</p>

        <div className="row" style={{ justifyContent: "center", gap: 5, margin: "16px 0" }}>
          {STEPS.map((_, n) => (
            <span
              key={n}
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: n === i ? "var(--accent)" : "var(--border-strong)",
              }}
            />
          ))}
        </div>

        <div className="row row-between">
          <button type="button" className="subtle" onClick={finish}>
            {t("wiz.skip")}
          </button>
          <div className="row" style={{ gap: 6 }}>
            {i > 0 && (
              <button type="button" onClick={() => setI(i - 1)}>
                {t("wiz.back")}
              </button>
            )}
            {i < STEPS.length - 1 ? (
              <button type="button" className="primary" onClick={() => setI(i + 1)}>
                {t("wiz.next")}
              </button>
            ) : (
              <button type="button" className="primary" onClick={finish}>
                {t("wiz.done")}
              </button>
            )}
          </div>
        </div>
        <div className="faint small" style={{ marginTop: 10 }}>
          {t("wiz.step", { n: i + 1, total: STEPS.length })}
        </div>
      </div>
    </div>
  );
}
